import { Box } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { addDays, compareDesc, format, isFuture, max } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { formatSalaryComponents } from "../../../../common/utils/formatSalaryComponents";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useNotification from "../../../../hooks/useNotification";
import { SalaryPerDate } from "../../../employment/employmentApi";
import {
  applyTesSalaryTableCell,
  clearEmploymentAttributesSalary,
  endEmploymentAttributesSalary,
  fetchEmploymentAttributesForEditing,
  selectEmploymentAttributesById,
  SetEmpAttrSalaryReducerParams,
  setNewEmpAttrSalary,
} from "../../../employment/employmentSlice";
import { Talent } from "../../../talents/talentAPI";
import { fetchTalentSelfServiceOrderPermissions } from "../../../talents/talentsSlice";
import { TesSalaryTableSelectionProvenance } from "../../../tesSalaryTables/tesSalaryTablesApi";
import { EditMode } from "../../TalentDetails";
import ActionButtons from "./ActionButtons";
import BasicInfo from "./BasicInfo";
import EditableSalary from "./EditableSalary";
import SalariesTable from "./SalariesTable";
import StopHiring from "./StopHiring";
import {
  applyTesSalaryTableSelectionToComponents,
  getAdditionalSalaryComponentsForTesTableApply,
  withFullSalaryMode,
  withInfoOnlyMode,
} from "./tesSalaryTableFormUtils";

type Props = {
  talent: Talent | null;
  onClose: () => void;
  editMode: EditMode;
};

const TalentEditShiftSalariesForm = ({ talent, onClose, editMode }: Props) => {
  const [selectedSalary, setSelectedSalary] = useState<SalaryPerDate | null>(
    null,
  ); // TODO: default selected
  const [selectedZone, setSelectedZone] = useState<string | undefined>(
    undefined,
  );
  const [editableSalary, setEditableSalary] = useState<SalaryPerDate | null>(
    null,
  );
  const [stopHiring, setStopHiring] = useState(false);
  const [selectedTableSelection, setSelectedTableSelection] =
    useState<TesSalaryTableSelectionProvenance | null>(null);
  const [isTesSalaryTableAvailable, setIsTesSalaryTableAvailable] =
    useState(false);

  const { tes, workTitle } = useAppSelector((state) => state.localized);
  // TODO: move tesSalaryComponents query from talentSlice to tesSlice

  // attributes currently edited
  const employmentAttributesForEditing = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );
  const { status } = useAppSelector((state) => state.employment.attributes);
  const isLoading = status === "pending";

  const tableData = [
    ...employmentAttributesForEditing.attributes.salaryPerDate,
  ].sort((a: SalaryPerDate, b: SalaryPerDate) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return compareDesc(dateA, dateB);
  });

  // calculate latest salary date first (needed for determining current salary)
  const latestSalary = useMemo(() => {
    const allDates = tableData.map((salary) => new Date(salary.date));
    const latestDate = max(allDates);
    return latestDate;
  }, [tableData]);

  // if salaryZones exist but salaryPerDate entries don't have zonedSalary data,
  // add empty zonedSalary data to enable zoned input fields
  // only for current and future salary dates, not for past salaries
  const processedTableData = useMemo(() => {
    const salaryZones = employmentAttributesForEditing.attributes?.salaryZones;
    if (!salaryZones || salaryZones.length === 0) {
      return tableData;
    }

    return tableData
      .map((salary) => {
        const salaryDate = new Date(salary.date);

        const isCurrentSalary =
          latestSalary &&
          salaryDate.getTime() === latestSalary.getTime() &&
          !isFuture(salaryDate);
        const isFutureSalary = isFuture(salaryDate);

        if (
          (isCurrentSalary || isFutureSalary) &&
          (!salary.zonedSalary || Object.keys(salary.zonedSalary).length === 0)
        ) {
          // add empty zonedSalary data for zones
          const zonedSalary: { [zone: string]: { [k: string]: string } } = {};
          salaryZones.forEach((zone) => {
            zonedSalary[zone as string] = {};
          });

          return {
            ...salary,
            zonedSalary,
          };
        }
        return salary;
      })
      .sort((a: SalaryPerDate, b: SalaryPerDate) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return compareDesc(dateA, dateB);
      });
  }, [
    tableData,
    latestSalary,
    employmentAttributesForEditing.attributes?.salaryZones,
  ]);

  const tableHeaders = [
    ...new Set(processedTableData.flatMap((item) => Object.keys(item.salary))),
  ];

  const dispatch = useAppDispatch();
  const { sendNotification } = useNotification();
  const { t } = useTranslation();

  const employmentItem = useAppSelector((state) =>
    selectEmploymentAttributesById(state, editMode.id),
  );

  const tesName = employmentItem ? tes[employmentItem?.tesId] : "";
  const workTitleName = employmentItem
    ? workTitle[employmentItem?.workTitleId]
    : "";
  const validFromDefaultValue = selectedSalary?.date
    ? new Date(selectedSalary?.date)
    : null;

  const methods = useForm({
    defaultValues: {
      tes: { name: tesName || "", id: employmentItem?.tesId || "" },
      validFrom: validFromDefaultValue,
      workTitle: {
        name: workTitleName || "",
        id: employmentItem?.workTitleId || "",
      },
      components: {},
      totalSalary: employmentItem?.totalSalary || "",
      tesSalaryTableSelection: null as TesSalaryTableSelectionProvenance | null,
      endDate: max([addDays(latestSalary, 1), new Date()]),
    },
    mode: "onChange",
  });

  const salaryComponents = methods.watch("components");
  const workTitleId = methods.watch("workTitle")?.id;
  const tesId = methods.watch("tes")?.id;

  useEffect(() => {
    if (talent?.id && workTitleId && tesId && !selectedSalary) {
      dispatch(
        fetchEmploymentAttributesForEditing({
          talentId: talent.id,
          workTitleId,
          tesId: tesId,
        }),
      );
    }
    // eslint-disable-next-line
  }, [workTitleId, tesId, talent?.id, dispatch]);

  /* Check for existing self service permissions */
  const [hasExistingPermission, setHasExistingPermission] = useState(false);
  const [isSelfServicePermissionLoading, setIsSelfServicePermissionLoading] =
    useState(false);
  const sendPermissionNotification = () => {
    sendNotification(
      t("talentProfile:existingPermissionFound"),
      t("talentProfile:checkSelfServicePermissions"),
    );
  };

  useEffect(() => {
    if (!talent?.id) return;
    setIsSelfServicePermissionLoading(true);
    dispatch(
      fetchTalentSelfServiceOrderPermissions({ talentId: talent.id }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const permissions = action.payload;
        const today = new Date();
        // TODO make sure that payload includes workTitleId and tesId
        // after backend implementation has been done
        const duplicatePermission = permissions.find(
          (permission: any) =>
            permission.workTitleId === workTitleId &&
            permission.tesId === tesId &&
            new Date(permission.permissionStart) <= today &&
            new Date(permission.permissionEnd) >= today,
        );
        duplicatePermission
          ? setHasExistingPermission(true)
          : setHasExistingPermission(false);
      }
      setIsSelfServicePermissionLoading(false);
    });
  }, [dispatch, talent?.id, tesId, workTitleId]);
  /* */

  const handleSelectSalary = (salaryItem: SalaryPerDate, zone?: string) => {
    methods.setValue("validFrom", new Date(salaryItem.date));
    setSelectedSalary(salaryItem);
    setSelectedZone(zone);
    setSelectedTableSelection(
      salaryItem.tesSalaryTableSelection
        ? { ...salaryItem.tesSalaryTableSelection }
        : null,
    );
  };

  const handleEditSalary = () => {
    if (selectedZone) {
      const zoneData = selectedSalary!.zonedSalary?.[selectedZone] || {};

      setEditableSalary({
        ...selectedSalary!,
        salary: zoneData,
      });
    } else {
      setEditableSalary(selectedSalary);
    }
    setStopHiring(false);
  };

  const handleStopHiring = () => {
    setStopHiring(true);
    setEditableSalary(null);
  };

  const handleEndSalary = (endDate: Date) => {
    if (!endDate) return null;

    const formattedEndDate = format(endDate, "yyyy-MM-dd");

    const params = {
      talentId: talent?.id as string,
      version: employmentAttributesForEditing?.attributes.version,
      workTitleId: employmentAttributesForEditing?.attributes.workTitleId,
      tesId: employmentAttributesForEditing?.attributes.tesId,
      from: formattedEndDate,
    };

    if (hasExistingPermission) sendPermissionNotification();

    dispatch(endEmploymentAttributesSalary(params)).then((action) => {
      if (isFulfilled(action)) {
        onClose();
      }
    });
  };

  const handleDeleteSalary = (date: string) => {
    const params = {
      talentId: talent?.id as string,
      version: employmentAttributesForEditing?.attributes.version,
      workTitleId: employmentAttributesForEditing?.attributes.workTitleId,
      tesId: employmentAttributesForEditing?.attributes.tesId,
      from: date,
    };

    if (hasExistingPermission) sendPermissionNotification();

    dispatch(clearEmploymentAttributesSalary(params));
  };

  const onSave = (data: FieldValues) => {
    if (!stopHiring && !editableSalary) return onClose();
    if (stopHiring) return handleEndSalary(data.endDate);

    if (
      data.validFrom &&
      data.workTitle?.id &&
      talent?.id &&
      data.tes?.id &&
      editableSalary
    ) {
      const date = format(data.validFrom, "yyyy-MM-dd");
      const newComponentValues = formatSalaryComponents(data.components);
      // console.log("newComponentValues: ", newComponentValues)

      if (employmentAttributesForEditing.attributes) {
        const canApplySelectedTableCellDirectly =
          isTesSalaryTableAvailable &&
          !!selectedTableSelection &&
          !selectedZone;

        const additionalSalary =
          getAdditionalSalaryComponentsForTesTableApply(newComponentValues);

        // console.log("additionalSalary: ", additionalSalary)
        if (canApplySelectedTableCellDirectly) {
          dispatch(
            applyTesSalaryTableCell({
              talentId: talent.id,
              version: employmentAttributesForEditing.attributes.version,
              workTitleId: data.workTitle.id,
              tesId: data.tes.id,
              from: date,
              tesSalaryTableId: selectedTableSelection.tesSalaryTableId,
              rowId: selectedTableSelection.rowId,
              columnId: selectedTableSelection.columnId,
              mode: selectedTableSelection.mode,
              salary:
                selectedTableSelection.mode === "INFO_ONLY"
                  ? newComponentValues
                  : additionalSalary,
              totalSalary: data?.totalSalary,
              // this id param tells redux we are editing...
              id: editMode.id,
              // ...(hasZonedComponents && {
              //   zonedSalary: formattedZonedSalary,
              //   zonedSalaryTotal: updatedZonedSalaryTotal,
              // }),
            }),
          ).then((action) => {
            if (isFulfilled(action)) {
              onClose();
            }
          });
          return;
        }

        let formattedData: SetEmpAttrSalaryReducerParams;

        if (selectedZone) {
          // update specific zone
          const updatedZonedSalary = {
            ...selectedSalary!.zonedSalary,
            [selectedZone]: newComponentValues,
          };

          const formattedZonedSalary = Object.fromEntries(
            Object.entries(updatedZonedSalary).map(([zone, components]) => [
              zone,
              zone === selectedZone
                ? components
                : formatSalaryComponents(components as { [k: string]: string }),
            ]),
          );

          const hasZonedComponents = Object.values(formattedZonedSalary).some(
            (components) =>
              Object.values(components as { [k: string]: string }).some(
                (value) => typeof value === "string" && value.trim() !== "",
              ),
          );

          // keep original default zone / "Muu Suomi" totalSalary from employment item
          const originalDefaultZoneTotal =
            employmentItem?.totalSalary || "0.00";

          // update only the selected zone total in zonedSalaryTotal
          const updatedZonedSalaryTotal = {
            ...(employmentItem?.zonedSalary || {}),
            [selectedZone]: data.totalSalary,
          };

          formattedData = {
            salary: formatSalaryComponents(selectedSalary!.salary),
            talentId: talent?.id,
            version: employmentAttributesForEditing?.attributes.version,
            workTitleId: data.workTitle.id,
            tesId: data.tes?.id,
            from: date,
            totalSalary: originalDefaultZoneTotal,
            id: editMode.id,
            ...(hasZonedComponents && {
              zonedSalary: formattedZonedSalary,
              zonedSalaryTotal: updatedZonedSalaryTotal,
            }),
          };
        } else {
          // update salary without zones / or default "Muu Suomi" zone
          const formattedZonedSalary = editableSalary.zonedSalary
            ? Object.fromEntries(
                Object.entries(editableSalary.zonedSalary).map(
                  ([zone, components]) => [
                    zone,
                    formatSalaryComponents(
                      components as { [k: string]: string },
                    ),
                  ],
                ),
              )
            : {};

          const hasZonedComponents =
            !!editableSalary.zonedSalary &&
            Object.values(editableSalary.zonedSalary).some((components) =>
              Object.values(components as { [k: string]: string }).some(
                (value) => typeof value === "string" && value.trim() !== "",
              ),
            );

          formattedData = {
            salary: newComponentValues,
            talentId: talent?.id,
            version: employmentAttributesForEditing?.attributes.version,
            workTitleId: data.workTitle.id,
            tesId: data.tes?.id,
            from: date,
            totalSalary: data?.totalSalary,
            id: editMode.id,
            ...(hasZonedComponents && {
              zonedSalary: formattedZonedSalary,
              zonedSalaryTotal: data.zonedTotalSalary,
            }),
          };
        }

        dispatch(setNewEmpAttrSalary(formattedData)).then((action) => {
          if (isFulfilled(action)) {
            onClose();
          }
        });
      }
    }
  };

  const onCancel = () => {
    setStopHiring(false);
    setEditableSalary(null);
    setSelectedSalary(null);
    setSelectedZone(undefined);
    setSelectedTableSelection(null);
    setIsTesSalaryTableAvailable(false);
  };

  const getSaveDisabledValue = () => {
    // TODO: add isLoading
    if (isLoading) return true;
    if (!stopHiring && !editableSalary) return false;

    if (stopHiring && (editableSalary || methods.formState.errors["endDate"]))
      return true;

    if (
      !stopHiring &&
      (isEmpty(methods.formState.dirtyFields) ||
        !isEmpty(methods.formState.errors) ||
        !methods.formState.isValid ||
        !salaryComponents)
    ) {
      return true;
    }

    return false;
  };

  const endDate = employmentAttributesForEditing.attributes?.ended
    ? format(
        new Date(employmentAttributesForEditing.attributes.ended),
        "dd.MM.yyyy",
      )
    : "";

  useEffect(() => {
    const provenance = selectedSalary?.tesSalaryTableSelection;
    if (!provenance) {
      if (!selectedSalary) {
        setSelectedTableSelection(null);
        setIsTesSalaryTableAvailable(false);
      }
      return;
    }

    setSelectedTableSelection(provenance);
  }, [selectedSalary]);

  const handleAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      setIsTesSalaryTableAvailable(isAvailable);
      if (!isAvailable) {
        setSelectedTableSelection(null);
        //methods.setValue("tesSalaryTableSelection", null);
      }
    },
    [setSelectedTableSelection],
  );

  return (
    <Box>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          {/* TODO: add isLoading */}
          <BasicInfo endDate={endDate} />
          {editableSalary && (
            <EditableSalary
              tesId={tesId}
              defaultValues={editableSalary.salary}
              hasTesSalaryTableSelection={!!selectedTableSelection}
              onManualBaseComponentChange={() => {
                if (!selectedTableSelection) return;
                const downgraded = withInfoOnlyMode(selectedTableSelection);
                setSelectedTableSelection(downgraded);
                methods.setValue("tesSalaryTableSelection", downgraded, {
                  shouldDirty: true,
                });
                methods.trigger("components");
              }}
              tesSalaryTableProps={{
                disabled: !!selectedZone,
                tesId,
                workTitleId,
                date: selectedSalary?.date
                  ? new Date(selectedSalary.date)
                  : null,
                value: selectedTableSelection,
                onAvailabilityChange: handleAvailabilityChange,
                onChange: ({ selection, salaryAmount }) => {
                  setSelectedTableSelection(withFullSalaryMode(selection));
                  methods.setValue(
                    "components",
                    applyTesSalaryTableSelectionToComponents({
                      components: methods.getValues("components") || {},
                      salaryAmount,
                    }),
                    {
                      shouldDirty: true,
                      shouldValidate: true,
                    },
                  );
                  methods.trigger("components");
                },
              }}
            />
          )}
          <Box>
            <OverlaySpinner
              isLoading={isLoading || isSelfServicePermissionLoading}
            />
            <SalariesTable
              tableHeaders={tableHeaders}
              tableData={processedTableData}
              handleSelectSalary={handleSelectSalary}
              handleDeleteSalary={handleDeleteSalary}
              selectedSalary={selectedSalary}
              selectedZone={selectedZone}
              disableRowChange={!!editableSalary || stopHiring}
            />
            {stopHiring && <StopHiring latestSalary={latestSalary} />}
          </Box>
          <ActionButtons
            edit={!editableSalary && !stopHiring}
            editDisabled={!selectedSalary}
            onCancel={onCancel}
            onEdit={handleEditSalary}
            onStop={handleStopHiring}
            stopDisabled={!!employmentAttributesForEditing.attributes?.ended}
            onSave={methods.handleSubmit(onSave)}
            saveDisabled={getSaveDisabledValue()}
          />
        </UnsavedChangesProvider>
      </FormProvider>
    </Box>
  );
};

export default TalentEditShiftSalariesForm;
